package com.crowpay.views.components.project.work.payApps

import com.crowpay.*
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.extensions.handleOne
import com.crowpay.extensions.isUnread
import com.crowpay.sdk.currentSession
import com.crowpay.sdk.lensUnread
import com.crowpay.sdk.notFoundError
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.*
import com.crowpay.utils.tables.trackWeights
import com.crowpay.views.components.actionIcon
import com.crowpay.views.components.forEachWithRenderer
import com.crowpay.views.components.leftExpandButtonWithUnread
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.nav.viewIsWide
import com.crowpay.views.components.project.activity.concerns.IncidentReportForm
import com.crowpay.views.components.project.work.scope.LineItemCompletionForm
import com.crowpay.views.components.scopeSetIcon
import com.crowpay.views.screens.common.ProjectLens
import com.crowpay.views.screens.common.ProjectView
import com.crowpay.views.components.sectionIndentCol
import com.crowpay.views.components.space
import com.crowpay.views.dialogs.AddProjectCreditDialog
import com.crowpay.views.dialogs.DrawWaitedDetails
import com.crowpay.views.dialogs.GenericConfirmationDialog
import com.crowpay.views.dialogs.ProjectCreditDetails
import com.crowpay.views.dialogs.WaitDrawsDialog
import com.crowpay.views.screens.common.ActionImportance
import com.crowpay.views.theming.*
import com.lightningkite.UUID
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.mainScreenNavigator
import com.lightningkite.kiteui.navigation.screenNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.RecyclerViewRenderer
import com.lightningkite.kiteui.views.l2.RecyclerViewRendererSet
import com.lightningkite.kiteui.views.l2.icon
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant
import kotlin.math.roundToLong

fun ProjectView.draws(writer: ViewWriter) = with(writer) {
    col {
        if (lens == ProjectLens.Contractor) atTopEnd - row {
            tertiaryButton - button {
                existsDefaultFalse { notNullSession().self().membership?.role == MemberRole.Admin }
                specCenteredText("+ Credit")
                onClick("Add Project Credit") {
                    dialogScreenNavigator.navigate(AddProjectCreditDialog(project()))
                }
            }
            tertiaryButton - button {
                ::enabled {
                    !project().state.isProjectLocked() && project().state != ProjectState.Accepted
                }
                specCenteredText {
                    if (payApps().any { it.submitted == null }) "Edit Draft" else "+ Draw"
                }
                onClick("Create new Draw") {
                    payApps().firstOrNull { it.submitted == null }?.let { screenNavigator.navigate(PayAppForm(it._id)) }
                        ?: screenNavigator.navigate(CreatePayApp(project()._id))
                }
            }
        }

        text {
            existsDefaultFalse {
                project().state == ProjectState.WaitingApproval
            }
            content = "This info will be available once the project is Awarded or In Progress."
        }

        text {
            existsDefaultFalse {
                project().state == ProjectState.Accepted
                        && lens == ProjectLens.Contractor
            }
            content = "You can create Draws after the project is started."
        }

        text {
            existsDefaultFalse {
                project().state == ProjectState.Accepted && lens == ProjectLens.Customer
            }
            content = "When contractor has started and is ready to submit a draw they will appear here."
        }

        col {
            if (viewIsWide) spacing = 0.dp
            val renderers = DrawTableRenderer(this@draws)
            forEachWithRenderer<DrawTableItem, Instant>(
                shared { (payApps() + project().projectCredits).sortedBy { it.timestamp } },
                renderers,
                loadingRenderers = List(5) { renderers.payAppRenderer }
            )
        }
    }
}


private class DrawTableRenderer(val projectView: ProjectView) : RecyclerViewRendererSet<DrawTableItem, Instant> {
    override fun id(item: DrawTableItem): Instant = item.timestamp

    private val cache = HashMap<UUID, Readable<List<LineItemWithPaymentInfo>>>()
    private fun paymentData(item: PayApplication) =
        cache.getOrPut(item._id) { shared { getPaymentData(projectView, item) } }

    val payAppRenderer = object : RecyclerViewRenderer<DrawTableItem> {
        override fun render(viewWriter: ViewWriter, data: Readable<DrawTableItem>, index: Readable<Int>): ViewModifiable = with(projectView) {
            data as Readable<PayApplication>

            val paymentData = shared { paymentData(data()).invoke() }

            if (viewWriter.viewIsNarrow) renderPayAppCard(data, paymentData, viewWriter)
            else renderPayApp2(data, paymentData, viewWriter)
        }
    }

    val creditRenderer = object : RecyclerViewRenderer<DrawTableItem> {
        override fun render(viewWriter: ViewWriter, data: Readable<DrawTableItem>, index: Readable<Int>): ViewModifiable = with (projectView) {
            data as Readable<Credit>

            return renderProjectCredit(data, viewWriter)
        }
    }

    override fun renderer(item: DrawTableItem): RecyclerViewRenderer<DrawTableItem> = when (item) {
        is Credit -> creditRenderer
        is PayApplication -> payAppRenderer
    }
}

private val rowHeight = 3.5.rem


private fun ProjectView.renderProjectCredit(
    credit: Readable<Credit>,
    writer: ViewWriter
) = with(writer) {
    suspend fun destination(): Page {
        val credit = credit()
        return when {
            credit.lineItem != null -> LineItemCompletionForm.createDialog(
                lineItems().find { it._id == credit.lineItem } ?: notFoundError<LineItem>(),
                project(),
                lens
            )

            credit.incident != null -> IncidentReportForm.createDialog(
                project(),
                incidents().find { it._id == credit.incident } ?: notFoundError<Incident>(),
                incidentMessages().filter { it.incident == credit.incident },
                lens
            )

            else -> {
                val client = client()
                val contractor = contractor()
                val userId = notNullSession().userId

                ProjectCreditDetails(credit, client, contractor, userId, project())
            }
        }
    }

    if (viewIsNarrow) lightSection - button {
        row {
            centered - boldSubTitle {
                ::content { "Credit ${credit().projectCreditGroup ?: 0}" }
            }
            expanding - centered - body {
                ::content { credit().amount.renderDollars() }
            }
            centered - DisplayableStateSemantic(ProjectCreditDetails.issuedState).onNext - body(ProjectCreditDetails.issuedState.displayName)
        }
        onClick("View Details") {
            mainScreenNavigator.navigate(destination())
        }
    }
    else col {
        spacing = 0.px
        padded - sizeConstraints(height = rowHeight) - row {
            spacing = 0.5.rem
            if (viewIsWide) space(1.65)
            expanding - compact - row {
                centered - subTitle {
                    ::content { "Credit ${credit().projectCreditGroup ?: 0}" }
                }
                centered - DisplayableStateSemantic(ProjectCreditDetails.issuedState).onNext - body(ProjectCreditDetails.issuedState.displayName)
            }
            centered - secondaryButton - button {
                spacing = 5.dp
                specCenteredTextWithUnread("View Details") { credit().isUnread(notNullSession().userId) }
                onClick("View Details") {
                    dialogScreenNavigator.navigate(destination())
                }
            }
        }
        greySeparator()
    }
}


private fun ProjectView.renderPayApp2(
    payApp: Readable<PayApplication>,
    items: Readable<List<LineItemWithPaymentInfo>>,
    writer: ViewWriter,
) = with(writer) {
    val expanded = Property(false)
    val status = shared {
        items().mapNotNull { it.payApp?.status }.toSet().overallStatus()
    }
    val pending = shared { items().filter { it.payApp?.status == PayAppStatus.Pending } }
    val groupedItems = shared {
        val scopes = scopes().associateBy { it._id }
        items()
            .groupBy { it.lineItem.scopeView }
            .mapKeys { (id, _) -> scopes[id] }
            .toList()
            .sortedBy { it.first?._id }
    }
    val selectedItems = Property<Set<UUID>>(setOf())
    val allSelected = shared {
        selectedItems().size == pending().size
    }
        .withWrite {
            if (it) selectedItems.value = pending().map { it.lineItem._id }.toSet()
            else selectedItems.value = emptySet()
        }
    val labelValue = shared {
        if (selectedItems().size == pending().size) {
            " All"
        } else if (selectedItems().size > 1 && selectedItems().size < pending().size) {
            " (${selectedItems().size})"
        } else {
            ""
        }
    }
    val payAppIsUnread = currentSession.lensUnread { session ->
        session.payApplications.handleOne { payApp() }
    }

    val feedbackUnread = currentSession.lensUnread { session ->
        session.payAppItems.feedbackIsUnread { items().mapNotNull { it.payApp } }
    }

    col {
        spacing = 0.rem
        sizeConstraints(height = rowHeight) - row {
            spacing = 0.rem
            dynamicTheme {
                if (expanded()) ThemeDerivation { it[SectionSemantic].theme.withBackNoPadding } else null
            }
            expanding - leftExpandButtonWithUnread(
                expanded,
                unread = shared { payAppIsUnread() + feedbackUnread() },
                highlightOnExpand = false
            ) {
                row {
                    spacing = 8.dp
                    row {
                        spacing = AppDimensions.expandButtonSpace
                        centered - actionIcon { if (lens.isClient() && items().any { it.payApp?.pending == true }) ActionImportance.Important else null }
                        centered - subTitle {
                            ::content { "Draw ${payApp().number}" }
                        }
                    }
                    centered - body {
                        ::content { status().displayName }
                        dynamicTheme { DisplayableStateSemantic(status()) }
                    }
                }
            }
            padded - stack {
                spacing = 0.5.rem
                existsDefaultFalse { status() != PayAppStatus.Draft }
                centered - secondaryButton - button {
                    specCenteredText { "View Details" }
                    spacing = 5.dp
                    onClick {
                        dialogScreenNavigator.navigate(
                            PayAppSummary(
                                payApp(),
                                project(),
                                scopes(),
                                items()
                            )
                        )
                    }
                }
            }
            row {
                existsDefaultFalse { status() == PayAppStatus.Draft }
                centered - buttonTheme - link {
                    spacing = 0.25.rem
                    ::to {
                        { PayAppForm(payApp()._id) }
                    }
                    centered - icon(Icon.edit.resize(1.5.rem), "Edit Change Order")
                }
                compact - centered - deleteButton - button {
                    centered - icon(Icon.trash.resize(1.5.rem), "Delete")
                    onClick {
                        dialogScreenNavigator.navigate(
                            GenericConfirmationDialog(
                                header = "Delete Draft?",
                                message = "You are about to delete this draw draft. Deleted drafts cannot be recovered. Delete this draft?",
                                confirmationText = "Delete Draft",
                                confirmationTheme = DangerButtonSemantic,
                                onSubmit = { confirmed ->
                                    if (confirmed) {
                                        notNullSession().payApplications[payApp()._id].delete()
                                    }
                                }
                            )
                        )
                    }
                }
            }
        }
        fun ViewWriter.thisCol(setup: RowOrCol.() -> Unit) = col(setup)
        onlyWhen { expanded() } - thisCol {
            trackWeights {
                val rowPadding = 0.5.rem
                spacing = 0.rem
                space()
                sectionIndentCol {
                    row {
                        fun ViewWriter.centeredHeader(text: String) = bodyBold { align = Align.Center; content = text }

                        setColWeight(6f) - atBottomCenter - bodyBold("Work Item")
                        setColWeight(1f) - atBottomCenter - centeredHeader("Work Value")
                        setColWeight(1f) - atBottomCenter - centeredHeader("Retention")
                        setColWeight(1f) - atBottomCenter - centeredHeader("Due")
                        setColWeight(1f) - atBottomCenter - centeredHeader("Payment")
                    }
                    greySeparator()
                }

                col {
                    spacing = 0.rem

                    fun ViewWriter.money(calculation: ReactiveContext.() -> Long?) = centered - body {
                        align = Align.Center
                        ::content { calculation()?.renderDollars() ?: "-" }
                    }

                    if (lens == ProjectLens.Customer) sizeConstraints(height = 2.5.rem) - col {
                        existsDefaultFalse { pending().isNotEmpty() }
                        spacing = 0.rem
                        sizeConstraints(height = rowPadding) - row()
                        row {
                            stack {
                                gravity(Align.Start, Align.Center) - checkbox {
                                    checked bind allSelected
                                }
                            }
                            centered - expanding - bodyBold("Select All")
                        }
                        sizeConstraints(height = rowPadding) - row()
                        sectionIndentCol { greySeparator() }
                        sizeConstraints(height = rowPadding) - row()
                    }

                    col {
                        forEachUpdating(groupedItems) { pair ->
                            val scope = shared { pair().first }
                            val lines = shared { pair().second }

                            col {
                                col {
                                    ::exists { scope() != null }
                                    space(0.25)
                                    compact - lightSection - row {
                                        centered - scopeSetIcon(1.rem) {
                                            ::exists { scope()?.scopeSet == true }
                                        }
                                        body { ::content { scope()?.scopeTitle ?: "" } }
                                    }
                                }

                                col {
                                    spacing = 0.rem
                                    forEachUpdating(lines) { lineItemWithPayment ->
                                        sizeConstraints(height = rowPadding) - row()
                                        row {
                                            dynamicTheme {
                                                if (lineItemWithPayment().lineItem.cancelled) GreyedOutSemantic else null
                                            }

                                            centered - row {
                                                ::visible {
                                                    lens == ProjectLens.Customer &&
                                                            pending().isNotEmpty() &&
                                                            lineItemWithPayment().payApp?.status == PayAppStatus.Pending
                                                }
                                                stack {
                                                    gravity(Align.Start, Align.Center) - checkbox {
                                                        checked bind selectedItems.contains(lineItemWithPayment.state.get().lineItem._id)
                                                    }
                                                }
                                            }

                                            centered - getColWeight - row {
                                                spacing = 5.dp
                                                centered - body {
                                                    ::content { lineItemWithPayment().name }
                                                }
                                                centered - smallBody {
                                                    ::content {
                                                        lineItemWithPayment().lineItem.changeType?.postTense ?: ""
                                                    }
                                                }
                                                onlyWhen {
                                                    lineItemWithPayment().payApp != null &&
                                                            !(items().mapNotNull { it.payApp }
                                                                .any { it.status != PayAppStatus.Pending } &&
                                                                    lineItemWithPayment().payApp?.status == PayAppStatus.Pending)
                                                } - centered - icon {
                                                    ::source {
                                                        when (lineItemWithPayment().payApp?.status) {
                                                            PayAppStatus.Denied -> Icon.close
                                                            PayAppStatus.Voided -> Icon.close
                                                            PayAppStatus.Approved -> Icon.done
                                                            else -> null
                                                        }
                                                    }
                                                    description = "Draw Item status"
                                                    dynamicTheme {
                                                        ForegroundSemantic(
                                                            lineItemWithPayment().payApp?.status?.color ?: Color.gray
                                                        )
                                                    }
                                                }
                                                onlyWhen {
                                                    items().mapNotNull { it.payApp }
                                                        .any { it.status != PayAppStatus.Pending } &&
                                                            lineItemWithPayment().payApp?.status == PayAppStatus.Pending
                                                } - sizeConstraints(width = 1.rem, height = 1.rem) - centered - row {
                                                    spacing = 0.rem
                                                    themeChoice += BackgroundSemantic(AppColors.accent.main) + MaxRadiiSemantic
                                                }
                                            }

                                            getColWeight - money { lineItemWithPayment().thisPayment }
                                            getColWeight - money {
                                                val payment = lineItemWithPayment().thisPayment ?: return@money null
                                                (payment * project().safeRetention).roundToLong()
                                            }
                                            getColWeight - money {
                                                val payment = lineItemWithPayment().thisPayment ?: return@money null
                                                payment - (payment * project().safeRetention).roundToLong()
                                            }
                                            getColWeight - money {
                                                lineItemWithPayment().let {
                                                    val draw = it.payApp ?: return@money null
                                                    when {
                                                        draw.status == PayAppStatus.Approved || selectedItems().contains(it.lineItem._id) ->
                                                            draw.amount - (draw.amount * project().safeRetention).roundToLong()

                                                        draw.status == PayAppStatus.Denied -> 0L
                                                        else -> null
                                                    }
                                                }
                                            }
                                        }
                                        sizeConstraints(height = rowPadding) - row()
                                        sectionIndentCol { greySeparator() }
                                    }
                                }
                            }
                        }
                    }
                }
                sectionIndentCol {
                    sizeConstraints(height = rowPadding) - row()
                    row {
                        fun ViewWriter.moneyBold(calculation: ReactiveContext.() -> Long) = bodyBold {
                            align = Align.Center
                            ::content { calculation().renderDollars() }
                        }

                        getColWeight - bodyBold("Total Requested")
                        getColWeight - moneyBold {
                            items().sumOf { it.thisPayment ?: 0 }
                        }
                        getColWeight - moneyBold {
                            (items().sumOf {
                                it.thisPayment ?: 0
                            } * project().safeRetention).roundToLong()
                        }
                        getColWeight - moneyBold {
                            items().sumOf { it.thisPayment ?: 0 } - (items().sumOf {
                                it.thisPayment ?: 0
                            } * project().safeRetention).roundToLong()
                        }
                        getColWeight - bodyBold {
                            content = "-"
                            align = Align.Center
                        }
                    }
                }
                sectionIndentCol {
                    space(rowPadding)
                    row {
                        val actual = shared {
                            items().filter { item ->
                                (item.payApp?.status == PayAppStatus.Approved) ||
                                selectedItems().contains(item.lineItem._id)
                            }
                        }

                        fun ViewWriter.moneyBold(calculation: ReactiveContext.() -> Long) = bodyBold {
                            align = Align.Center
                            ::content { calculation().renderDollars() }
                        }

                        getColWeight - bodyBold("Actuals")
                        getColWeight - moneyBold {
                            actual().sumOf { it.thisPayment ?: 0 }
                        }
                        getColWeight - moneyBold {
                            (actual().sumOf {
                                it.thisPayment ?: 0
                            } * project().safeRetention).roundToLong()
                        }
                        getColWeight - bodyBold {
                            align = Align.Center
                            content = "-"
                        }
                        getColWeight - moneyBold {
                            actual().sumOf { it.thisPayment ?: 0 } - (actual().sumOf {
                                it.thisPayment ?: 0
                            } * project().safeRetention).roundToLong()
                        }
                    }
                }
            }

            launch {
                if (lens == ProjectLens.Customer) selectedItems set pending().map { it.lineItem._id }.toSet()
                JumpTo.PayApp.newTarget(payApp()._id, this@thisCol) { expanded.value = true }
            }

            space()
            atEnd - row {
                secondaryButton - button {
                    existsDefaultFalse { items().any { it.payApp?.denied != null } }
                    specCenteredTextWithUnread("View Concerns") { feedbackUnread()() }
                    onClick {
                        val items = items()
                        dialogScreenNavigator.navigate(
                            DrawWaitedDetails(
                                payApp(),
                                items.mapNotNull { it.payApp },
                                items.associate { it.lineItem._id to it.lineItem },
                                client().name,
                                contractor().name
                            )
                        )
                    }
                }
                if (lens == ProjectLens.Contractor) DangerButtonSemantic.onNext - button {
                    ::exists { pending().isNotEmpty() && pending().all { it.payApp?.status == PayAppStatus.Pending } }
                    specCenteredText("Void")
                    onClick("Void Draw") {
                        dialogScreenNavigator.navigate(
                            GenericConfirmationDialog(
                                header = "Void Draw",
                                message = "Are you sure you want to void the remaining draw items in this draw? This cannot be undone.",
                                confirmationText = "Void",
                                confirmationTheme = DangerButtonSemantic,
                                onSubmit = { confirmed ->
                                    if (confirmed) {
                                        val payItems = items()
                                        val session = notNullSession()
                                        val endpoints = session.nonCached.payAppItem
                                        coroutineScope {
                                            payItems
                                                .mapNotNull { it.payApp }
                                                .filter { it.status == PayAppStatus.Pending }
                                                .forEach {
                                                    launch {
                                                        val result = endpoints.voidPayApp(it._id)
                                                        session.payAppItems.localInsert(result)
                                                    }
                                                }
                                        }
                                    }
                                }
                            )
                        )
                    }
                }
                if (lens == ProjectLens.Customer) {
                    primaryButton - button {
                        ::exists { pending().isNotEmpty() }
                        ::enabled { selectedItems().isNotEmpty() }
                        specCenteredText { "Pay ${labelValue()}" }
                        onClick("Pay Selected Draws") {
                            val selected = pending().filter { it.lineItem._id in selectedItems() }
                            val total = selected.sumOf { it.thisPayment ?: 0 }
                            val retention = (total * project().safeRetention).roundToLong()
                            val totalMinusRetention = (total - retention).renderDollars()
                            dialogScreenNavigator.navigate(
                                GenericConfirmationDialog(
                                    header = if (allSelected()) "Pay Draw" else "Pay ${selected.size} Draw Items",
                                    message = "Pay $totalMinusRetention out of escrow to contractor?",
                                    confirmationText = "Pay $totalMinusRetention",
                                    onSubmit = { confirmed ->
                                        if (confirmed) {
                                            val payItems = selected.mapNotNull { it.payApp?._id }
                                            notNullSession().nonCached.payAppItem.approvePayApps(payItems)
                                            selectedItems set (setOf())
                                        }
                                    }
                                )
                            )
                        }
                    }
                    dangerButton - button {
                        ::exists { pending().isNotEmpty() }
                        ::enabled { selectedItems().isNotEmpty() }
                        specCenteredText { "Wait ${labelValue()}" }
                        onClick("Wait Selected Draws") {
                            val selected = pending().filter { it.lineItem._id in selectedItems() }
                            dialogScreenNavigator.navigate(
                                WaitDrawsDialog(
                                    draws = selected.toList(),
                                    header = if (allSelected()) "Wait Draw?" else "Wait ${selected.size} Draw Items",
                                ) { value ->
                                    selectedItems set (selectedItems().filterNot { it == value }.toSet())
                                }
                            )
                        }
                    }
                }
            }
            space(2.0)
        }
        greySeparator()
    }
}
